// Persisting one table's view — the filter chips, the search terms, the
// ordering, the page and which rows are expanded — so that clicking into a loan
// and coming back doesn't throw all of it away.
//
// sessionStorage rather than localStorage, on purpose. The default assignment
// chip embeds the signed-in user's OID (see defaultFilters.ts), so a stored view
// is coupled to the identity that produced it. MSAL already keeps its cache in
// sessionStorage, so the two share one lifetime: sign out, or open a new tab,
// and both reset together with no code remembering to do it. Per-tab also
// matches the work — two tabs on two different queues is normal here, and
// localStorage would have them overwrite each other.
//
// See docs/loans-list-cache-design.md.
import { operatorsFor, type Filter, type Operator, type SortSpec } from './filterModel';
import type { Column } from './columns';

/**
 * Bump when the stored shape stops being readable by this code — a renamed
 * column set, a changed `Operator` union, a new required field. Anything under
 * an older version is dropped wholesale rather than migrated; a lost filter chip
 * is a shrug, and migration code for a per-tab cache would outlive its value.
 */
const VERSION = 1;
const KEY_PREFIX = 'umwsb.view';

const storageKey = (name: string) => `${KEY_PREFIX}.${name}.v${VERSION}`;

/** The view state one table owns. Every field optional — writers patch a slice. */
export type ViewState = {
  filters: Filter[];
  quick: string;
  findAll: string;
  sort: SortSpec | null;
  page: number;
  /** Expanded row ids. A Set in the page, an array on the wire. */
  expanded: number[];
  /**
   * OID of the user the stored filters were built for.
   *
   * The default assignment chip embeds an OID (defaultFilters.ts), so a stored
   * view belongs to one identity and means something different under another —
   * "my assignments" for somebody you are no longer signed in as matches nothing.
   * Kept so a restored view can be recognised as someone else's and re-seeded
   * rather than silently applied.
   */
  identity: string;
};

// ---------------------------------------------------------------------------
// Validation
//
// Stored state is untrusted input written by an older build of this app. Nothing
// here can throw on garbage: the caller's alternative is a blank grid.
//
// Worth being precise about what the failure actually looks like, because it is
// not a crash. A chip naming a column that no longer exists resolves to
// `undefined` through `columnOf`, which `applyFilters` explicitly tolerates and
// `FilterBar` renders as nothing — so an unvalidated stale chip is *invisible
// but still stored*, silently filtering nothing forever. That is the bug this
// pass exists to prevent, and it is a hygiene problem rather than a safety one.
// ---------------------------------------------------------------------------

const isStr = (v: unknown): v is string => typeof v === 'string';

/**
 * A stored chip, kept only if the current build still understands every part of
 * it, labels included — see `toStored` for why they are stored at all.
 */
function validFilter(raw: unknown, columnOf: (id: string) => Column | undefined): Filter | null {
  if (!raw || typeof raw !== 'object') return null;
  const f = raw as Record<string, unknown>;
  if (!isStr(f.columnId) || !isStr(f.op) || !isStr(f.key)) return null;
  if (!Array.isArray(f.values) || !f.values.every(isStr)) return null;

  const column = columnOf(f.columnId);
  if (!column) return null;
  // An operator valid for some other kind of column is as unusable as one that
  // no longer exists — `matches` would fall through to a branch that means
  // something else entirely for this column's kind.
  if (!operatorsFor(column).includes(f.op as Operator)) return null;

  const out: Filter = {
    key: f.key, columnId: f.columnId, op: f.op as Operator, values: f.values as string[],
  };
  // Only string->string entries, and only for values this chip actually filters on: a
  // label for a value that is not here has nothing to display and is dead weight.
  if (f.labels && typeof f.labels === 'object' && !Array.isArray(f.labels)) {
    const raw = f.labels as Record<string, unknown>;
    const labels: Record<string, string> = {};
    for (const v of out.values) if (isStr(raw[v])) labels[v] = raw[v] as string;
    if (Object.keys(labels).length > 0) out.labels = labels;
  }
  return out;
}

function validSort(raw: unknown, columnOf: (id: string) => Column | undefined): SortSpec | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  if (!isStr(s.id) || (s.dir !== 'asc' && s.dir !== 'desc')) return null;
  // A sort on a column this table no longer shows is dropped rather than kept:
  // `useTableView` would fall back to the default ordering anyway, and storing
  // it would keep re-proposing an ordering nothing can display an arrow for.
  return columnOf(s.id) ? { id: s.id, dir: s.dir } : null;
}

/**
 * Coerce whatever was stored into a usable partial view. Unreadable fields are
 * dropped individually — one bad chip does not cost the user their sort.
 */
export function decodeViewState(
  raw: unknown,
  columnOf: (id: string) => Column | undefined,
): Partial<ViewState> {
  if (!raw || typeof raw !== 'object') return {};
  const o = raw as Record<string, unknown>;
  const out: Partial<ViewState> = {};

  if (Array.isArray(o.filters)) {
    out.filters = o.filters
      .map(f => validFilter(f, columnOf))
      .filter((f): f is Filter => f !== null);
  }
  if (isStr(o.quick)) out.quick = o.quick;
  if (isStr(o.identity)) out.identity = o.identity;
  if (isStr(o.findAll)) out.findAll = o.findAll;
  if ('sort' in o) out.sort = validSort(o.sort, columnOf);
  if (typeof o.page === 'number' && Number.isInteger(o.page) && o.page >= 1) out.page = o.page;
  if (Array.isArray(o.expanded)) {
    out.expanded = o.expanded.filter((n): n is number => typeof n === 'number' && Number.isInteger(n));
  }
  return out;
}

/**
 * What actually goes to storage.
 *
 * `labels` is kept, having first been stripped on the theory that `FilterBar`
 * re-derives a missing one from the loaded rows. It does — but only for values
 * that appear in some loaded row, because `valueOptions` builds its option list
 * out of the rows themselves. The assignment chip is exactly the case that fails:
 * its value is an OID and its label is a display name, so a user with no loans
 * assigned to them has no row carrying their OID, no option, no label, and the
 * chip renders a raw GUID. That is the default chip on every grid, and it broke
 * on any refresh or return from a loan.
 *
 * `defaultFiltersFor` already supplies labels up front for this reason. Dropping
 * them on write discarded the one thing making the chip legible. The cost is a
 * display name in sessionStorage — beside MSAL's own cache of the same name — and
 * a label going stale if someone is renamed mid-session, which is cosmetic and
 * bounded by the session.
 */
export function toStored(state: Partial<ViewState>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...state };
  if (state.filters) {
    out.filters = state.filters.map(({ key, columnId, op, values, labels }) => ({
      key, columnId, op, values, ...(labels ? { labels } : {}),
    }));
  }
  return out;
}

// ---------------------------------------------------------------------------
// Storage
//
// Every access is guarded: sessionStorage throws on access in some private
// modes, and losing a view is never worth taking the page down.
// ---------------------------------------------------------------------------

/**
 * Forget every remembered view.
 *
 * Called when the signed-in identity changes — which for this app means the
 * impersonation switcher. A stored view outliving its identity is not merely
 * stale, it is unsatisfiable: the assignment chip names a user you are not, so
 * the grid comes back empty and the filter bar offers no clue why. Clearing at
 * the moment of the switch is cheaper than teaching every reader to re-check.
 */
export function clearAllViewState(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(`${KEY_PREFIX}.`)) doomed.push(k);
    }
    for (const k of doomed) sessionStorage.removeItem(k);
  } catch {
    /* storage unavailable — nothing was stored to clear */
  }
}

export function readViewState(
  name: string,
  columnOf: (id: string) => Column | undefined,
): Partial<ViewState> {
  try {
    const raw = sessionStorage.getItem(storageKey(name));
    return raw ? decodeViewState(JSON.parse(raw), columnOf) : {};
  } catch {
    return {};
  }
}

/**
 * Merge a slice into the stored view.
 *
 * Read-merge-write because the blob has two writers — `useTableView` owns the
 * filters, search terms and ordering, while the page owns the page number and
 * expanded rows. Whole-blob writes would have each side erase the other's half
 * on every keystroke.
 */
export function patchViewState(name: string, patch: Partial<ViewState>): void {
  try {
    const key = storageKey(name);
    const existing = sessionStorage.getItem(key);
    const base = existing ? JSON.parse(existing) as Record<string, unknown> : {};
    sessionStorage.setItem(key, JSON.stringify({ ...base, ...toStored(patch) }));
  } catch {
    /* storage unavailable — the view just won't survive the next navigation */
  }
}
